import { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col } from "react-flexbox-grid";
import { SelectBox } from "components/uikit-adapter/index";
import intl from "react-intl-universal";
import { fetchMaterialWithProductionActiveByLabeledFamilyCenter } from "../../../services/material.service";
import { fetchSupplierForMaterialAndUserLoggedGroup } from "../../../services/supplier.service";
import { formatErrorMessage } from "utils/handle-error";
import moment from "moment";
import { ISO_DATE_FORMAT } from "utils/format-date";

import { validatedDto } from "./validate";
import InputNumber from "components/input-number";
import { parseFloatByLocale } from "locales/utils";
import PropTypes from "prop-types";
import Input from "components/input";
import TextArea from "components/textarea";
import Button from "components/button";
import { fetchSuggestionsJustifications } from "services/suggestion/suggestion.service";
import "./aceite-volume-parcial-fornecedor.css";
import DeadlineControl from "components/deadline-control";

const UNLOCKED_TT = -99;

const subDays = (dateStr, days) =>
  moment(dateStr, ISO_DATE_FORMAT).subtract(days, "days").format(ISO_DATE_FORMAT);

const AceiteVolumeParcialFornecedor = ({
  idFamiliaRotuladaCentro,
  data,
  idPedido,
  handleCancel,
  showFeedback,
  onSave,
  showJustification: showJustificationProp = false,
}) => {
  const [transitTime, setTransitTime] = useState(UNLOCKED_TT);
  const [deliveryTime, setDeliveryTime] = useState(data.HoraEntrega);
  const [note, setNote] = useState("");
  const [collectionDate, setCollectionDate] = useState(data.DataColetaPrevista);
  const [deliveryDate, setDeliveryDate] = useState(data.DataEntregaPrevista);
  const [quantity, setQuantity] = useState(data.Quantidade);
  const [materials, setMaterials] = useState([]);
  const [material, setMaterial] = useState(data.IdMaterial);
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState(data.IdFornecedor);
  const [showJustification, setShowJustification] = useState(showJustificationProp);
  const [justifications, setJustifications] = useState([]);
  const [justification, setJustification] = useState(data.TipoJustificativaSugestaoManual);
  const [lockVersion, setLockVersion] = useState(0);

  const lockKey = useMemo(
    () => `blocked_deadlinecontrol-pedido-${idPedido}`,
    [idPedido]
  );

  const isDeadlineUnlocked = useCallback(
    () => localStorage.getItem(lockKey) === "false",
    [lockKey]
  );

  const lockDeadline = useCallback(() => {
    localStorage.setItem(lockKey, "true");
    setLockVersion((v) => v + 1);
  }, [lockKey]);

  const getMaterials = useCallback(async () => {
    try {
      const _materials =
        await fetchMaterialWithProductionActiveByLabeledFamilyCenter(
          idFamiliaRotuladaCentro
        );
      setMaterials(_materials);
    } catch (e) {
      showFeedback(formatErrorMessage(e));
    }
  }, [idFamiliaRotuladaCentro, showFeedback]);

  const getSuppliers = useCallback(async () => {
    try {
      const _suppliers = await fetchSupplierForMaterialAndUserLoggedGroup(material);
      setSuppliers(_suppliers);
    } catch (e) {
      showFeedback(formatErrorMessage(e));
    }
  }, [material, showFeedback]);

  const getJustifications = useCallback(async () => {
    try {
      const _justifications = await fetchSuggestionsJustifications();
      setJustifications(_justifications);
    } catch (e) {
      showFeedback(formatErrorMessage(e));
    }
  }, [showFeedback]);

  useEffect(() => {
    getMaterials();
    getSuppliers();
    getJustifications();
  }, [getMaterials, getSuppliers, getJustifications]);

  useEffect(() => {
    const current = suppliers.find((s) => s.Id === supplier);
    setTransitTime(current?.TransitionTime ?? 0);
  }, [supplier, suppliers]);

  useEffect(() => {
    if (transitTime !== UNLOCKED_TT && isDeadlineUnlocked()) {
      lockDeadline();
    }
  }, [transitTime, isDeadlineUnlocked, lockDeadline]);

  useEffect(() => {
    if (!deliveryDate) return;
    if (transitTime <= UNLOCKED_TT) return;      
    if (isDeadlineUnlocked()) return;             
    const next = subDays(deliveryDate, transitTime);
    if (next !== collectionDate) setCollectionDate(next);
  }, [deliveryDate, transitTime, collectionDate, isDeadlineUnlocked]);

  const handleChangeMaterial = useCallback(async (value) => {
    const newId = value?.Id ?? null;
    setMaterial(newId);

    if (newId) {
      const _sup = await fetchSupplierForMaterialAndUserLoggedGroup(newId);
      setSuppliers(_sup);
    } else {
      setSuppliers([]);
    }
  }, []);

  const handlerChangeSupplier = useCallback((value) => {
    setSupplier(value?.Id ?? null);
    if (isDeadlineUnlocked()) lockDeadline();
  }, [isDeadlineUnlocked, lockDeadline]);

  const handleChangeQuantity = useCallback((value) => {
    setShowJustification(true);
    setQuantity(value);
  }, []);

  const handleChangeDeadline = useCallback((coleta, entrega) => {
    const isColetaChanged = coleta !== collectionDate;
    const isEntregaChanged = entrega !== deliveryDate;
    const isTTUnlocked = transitTime === UNLOCKED_TT;
    const isLocked = !isDeadlineUnlocked();

    let nextCollection = coleta;
    let nextDelivery = entrega;

    if (!isLocked && transitTime !== UNLOCKED_TT && !isColetaChanged && isEntregaChanged) {
      nextCollection = collectionDate;
    }

    const shouldShow =
      (isTTUnlocked && isEntregaChanged) ||
      (!isTTUnlocked &&
        ((isLocked && (isColetaChanged || isEntregaChanged)) ||
         (!isLocked && isEntregaChanged)));

    if (shouldShow) setShowJustification(true);

    setCollectionDate(nextCollection);
    setDeliveryDate(nextDelivery);
  }, [collectionDate, deliveryDate, transitTime, isDeadlineUnlocked]);

  const identificadorDeadlineControl = useCallback(
    () => `pedido-${idPedido}`,
    [idPedido]
  );

  const handleSave = useCallback(() => {
    const dto = {
      acceptVolumeOrder: false,
      volumeOrder: {
        Id: idPedido,
        VolumeAceito: parseFloatByLocale(quantity),
        Observacao: note,
        DataColetaProposta: collectionDate,
        DataEntregaProposta: deliveryDate,
        HoraEntregaProposta: deliveryTime,
        IdMaterial: material,
        IdFornecedor: supplier,
        TypeofJustificationSuggestionManual: justification,
      },
    };

    const validate = validatedDto(dto, showJustification);
    if (!validate.isValid) {
      showFeedback(validate.message);
      return;
    }

    onSave(dto);
  }, [
    idPedido,
    quantity,
    note,
    collectionDate,
    deliveryDate,
    deliveryTime,
    material,
    supplier,
    justification,
    showJustification,
    showFeedback,
    onSave,
  ]);

  return (
    <div className="negotiation">
      <div className="negotiation-line">
        <Row middle="xs">
          <Col xs={12}>
            <DeadlineControl
              key={`dc-${idPedido}-${lockVersion}`}
              TituloColeta={intl.get("stocks.suggestionsListItem.collect")}
              TituloEntrega={intl.get("stocks.suggestionsListItem.issue")}
              TransitTime={transitTime}
              OnChange={handleChangeDeadline}
              Coleta={collectionDate}
              DataEntregaDe={deliveryDate}
              Identificador={identificadorDeadlineControl()}
              noLockOption={transitTime === UNLOCKED_TT}
              showSeparator={transitTime !== UNLOCKED_TT}
            />
          </Col>
        </Row>
      </div>

      <div className="negotiation-line">
        <Row>
          <Col xs={12}>
            <SelectBox
              name="material"
              label={intl.get("commons.material")}
              placeholder={intl.get("commons.material")}
              valueKey="Id"
              labelKey="DescricaoCompleta"
              value={material}
              options={materials}
              onChange={handleChangeMaterial}
              searchable
              openOnFocus
              clearable
              data-testid="selectbox-material"
            />
          </Col>
        </Row>
      </div>

      <div className="negotiation-line">
        <Row>
          <Col xs={12}>
            <SelectBox
              name="supplier"
              label={intl.get("commons.supplier")}
              placeholder={intl.get("commons.supplier")}
              valueKey="Id"
              labelKey="Description"
              value={supplier}
              options={suppliers}
              onChange={handlerChangeSupplier}
              searchable
              openOnFocus
              clearable
              data-testid="selectbox-supplier"
            />
          </Col>
        </Row>
      </div>

      <div className="negotiation-line">
        <Row>
          <Col xs={6}>
            <InputNumber
              type="default"
              name="quantidade"
              label={intl.get("commons.amount")}
              value={quantity}
              onChange={handleChangeQuantity}
              data-testid="input-quantity"
            />
          </Col>
          <Col xs={6}>
            <Input
              label={intl.get("commons.deliveryTime")}
              name="HoraEntrega"
              htmlType="time"
              value={deliveryTime}
              onChange={(e) => setDeliveryTime(e.target.value)}
              className="txtHoraEntrega"
              data-testid="input-hour"
            />
          </Col>
        </Row>
      </div>

      {showJustification && (
        <div className="negotiation-line">
          <Row>
            <Col xs={12}>
              <SelectBox
                label={intl.get("commons.justification")}
                placeholder={intl.get("commons.justification")}
                valueKey="Key"
                labelKey="Value"
                value={justification}
                options={justifications}
                onChange={(value) => setJustification(value.Key)}
                data-testid="selectbox-justification"
              />
            </Col>
          </Row>
        </div>
      )}

      <div className="negotiation-line">
        <Row>
          <Col xs={12}>
            <TextArea
              name="observacao"
              label={intl.get("manual-suggestion-order-form.observation")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="input-note"
            />
          </Col>
        </Row>
      </div>

      <div className="negotiation-line">
        <div className="event-action-offer">
          <Row>
            <Col xs={6}>
              <Button
                value={intl.get("geral.buttonsDefault.close")}
                onClick={handleCancel}
                buttonType="default"
                className="button"
              />
            </Col>
            <Col xs={6}>
              <Button
                value={intl.get("commons.confirmChanges")}
                onClick={handleSave}
                buttonType="primary"
                className="button"
              />
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

AceiteVolumeParcialFornecedor.propTypes = {
  idFamiliaRotuladaCentro: PropTypes.string,
  data: PropTypes.shape({
    DataColetaPrevista: PropTypes.string,
    DataEntregaPrevista: PropTypes.string,
    Quantidade: PropTypes.number,
    IdMaterial: PropTypes.string,
    IdFornecedor: PropTypes.string,
    TipoJustificativaSugestaoManual: PropTypes.number,
    HoraEntrega: PropTypes.string,
  }),
  idPedido: PropTypes.string,
  handleCancel: PropTypes.func,
  showFeedback: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  showJustification: PropTypes.bool,
};

export default AceiteVolumeParcialFornecedor;